/**
 * Register a clinic.
 *
 * Everything about the tenant is entered here and created in one request: the
 * clinic, the article defaults its content inherits, and its stores. The server
 * writes them in a single transaction, so a failure leaves nothing half-made.
 *
 * The plan is chosen here from JMC's own catalogue - nothing about it is
 * compiled in - and leaving it on 「最安のプラン」 hands the choice to the server,
 * which takes the cheapest active one.
 *
 * The shape of the screen is four numbered steps, and that is not decoration.
 * Registration is one long form of unlike things - an identity, a contract, a
 * legal record, a set of branches - and as one flat run of fields it read as a
 * pile of boxes with no order: an operator could not tell what was required
 * from what was optional, nor where they were up to. Numbered steps say how
 * much is left; the one-line note under each says what the step is FOR, which
 * is what let most of the per-field prose go.
 *
 * Two rules the fields follow:
 *
 *   Required is marked; optional is not explained. Every field here was
 *   carrying a sentence, so none of them stood out. A hint now earns its place
 *   only where the value has a consequence somebody cannot guess - the plan
 *   caps generation, keywords reach every article.
 *
 *   Anything with a sensible default is not asked. Timezone was a dropdown of
 *   four zones on a product sold in Japan; it is Asia/Tokyo, set by the column
 *   default, and the day a clinic is somewhere else is the day it becomes a
 *   field on the clinic screen rather than a question at registration.
 */
import { useState, type FormEvent } from 'react';
import { useLoaderData, useNavigate } from 'react-router';
import { ApiError, post, type AdminPlan, type ListResponse } from '../api.ts';
import { Card, limitText } from '../components/ui.tsx';
import { Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { KeywordInput } from '../components/KeywordInput.tsx';
import { ReadFromSite } from '../components/ReadFromSite.tsx';
import {
  ClinicProfileFields,
  StoreProfileFields,
  clinicProfilePayload,
  storeProfilePayload,
  type ClinicProfileForm,
  type StoreProfileForm,
} from '../components/ProfileFields.tsx';

interface Created {
  id: string;
  name: string;
  stores: Array<{ id: string; name: string }>;
}

/** What the article generator falls back to when a store says nothing else. */
const TONES = [
  { value: 'polite', label: 'ていねい（敬体）' },
  { value: 'friendly', label: '親しみやすい' },
  { value: 'professional', label: '専門的' },
];

/**
 * One step of the form.
 *
 * A number, a name, and one line saying what the step is for. The number is
 * the point: it tells somebody halfway down the page how much of this there
 * is, which a run of bold headings never does.
 */
function Step({
  n,
  title,
  note,
  children,
}: {
  n: number;
  title: string;
  note: string;
  children: React.ReactNode;
}) {
  return (
    <section className="formstep">
      <div className="formstep__head">
        <span className="formstep__n" aria-hidden>
          {n}
        </span>
        <div>
          <h2 className="formstep__title">{title}</h2>
          <p className="formstep__note">{note}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

/** A store as this form holds it: a name, plus the details if anyone typed any. */
interface StoreDraft extends StoreProfileForm {
  name: string;
  wordpressCategory: string;
  /** Whether the detail fields are showing. Not sent anywhere. */
  open: boolean;
}

const emptyStore = (): StoreDraft => ({
  name: '',
  postalCode: '',
  prefecture: '',
  city: '',
  address: '',
  accessNote: '',
  phone: '',
  hoursNote: '',
  closedNote: '',
  reservationUrl: '',
  mapUrl: '',
  wordpressCategory: '',
  open: false,
});

/** Whether a row carries anything beyond its name. */
const hasDetails = (store: StoreDraft): boolean =>
  Object.entries(store).some(
    ([key, value]) => key !== 'name' && key !== 'open' && typeof value === 'string' && value !== '',
  );

export default function AdminClinicNew() {
  const navigate = useNavigate();
  const { plans } = useLoaderData<{ plans: ListResponse<AdminPlan> }>();

  const [name, setName] = useState('');
  const [siteName, setSiteName] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [defaultTone, setDefaultTone] = useState('polite');
  const [wordpressCategory, setWordpressCategory] = useState('');
  const [defaultKeywords, setDefaultKeywords] = useState<string[]>([]);
  const [profile, setProfile] = useState<ClinicProfileForm>({
    corporateName: '',
    representative: '',
    prefecture: '',
    city: '',
    address: '',
    phone: '',
    adManagerName: '',
  });
  // One blank row to start, so the first store needs no extra click.
  const [stores, setStores] = useState<StoreDraft[]>([emptyStore()]);
  /*
   * '' means "whichever plan is cheapest", which is what the API does with an
   * absent planId. Offered rather than assumed: the catalogue is JMC's own, so
   * the plan a clinic starts on is a decision somebody should be able to make
   * here instead of correcting on the next screen.
   */
  const [planId, setPlanId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * What the site reader is allowed to touch, and where each value goes.
   *
   * Written out rather than spread blindly over the form state: the endpoint
   * answers with whatever it could quote, and a field arriving that this form
   * does not have should be ignored here rather than silently widening what a
   * page can set. Labels are the same words the fields carry, so the 「どこから
   * 読み取ったか」 list names them the way the operator sees them.
   */
  const SITE_LABELS: Record<string, string> = {
    name: 'クリニック名',
    siteName: 'サイト名',
    corporateName: '法人名',
    representative: '代表者名',
    prefecture: '都道府県',
    city: '市区町村',
    address: '住所',
    phone: '電話番号',
  };

  function fillFromSite(fields: Record<string, string>) {
    if (fields.name) setName(fields.name);
    if (fields.siteName) setSiteName(fields.siteName);
    setProfile((current) => ({
      ...current,
      ...(fields.corporateName ? { corporateName: fields.corporateName } : {}),
      ...(fields.representative ? { representative: fields.representative } : {}),
      ...(fields.prefecture ? { prefecture: fields.prefecture } : {}),
      ...(fields.city ? { city: fields.city } : {}),
      ...(fields.address ? { address: fields.address } : {}),
      ...(fields.phone ? { phone: fields.phone } : {}),
    }));
  }

  const setStore = (index: number, next: Partial<StoreDraft>) =>
    setStores((current) =>
      current.map((store, at) => (at === index ? { ...store, ...next } : store)),
    );
  const addStore = () => setStores((current) => [...current, emptyStore()]);
  const removeStore = (index: number) =>
    setStores((current) =>
      current.length === 1 ? [emptyStore()] : current.filter((_, at) => at !== index),
    );

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const clinic = await post<Created>('/admin/clinics', {
        name,
        siteName,
        // A blank optional field is "not set", not an empty string the schema
        // would then reject for failing the https pattern.
        ...(siteUrl.trim() ? { siteUrl: siteUrl.trim() } : {}),
        defaultTone,
        wordpressCategory,
        // Sent even when empty: the column is NOT NULL and the route defaults it,
        // so an empty list here and an absent field mean the same thing.
        defaultKeywords,
        ...clinicProfilePayload(profile),
        ...(planId ? { planId } : {}),
        /*
         * A name alone goes as a string, which is the shape most registrations
         * have and the one the API has always taken. A row somebody filled in
         * goes as an object with those details, so the store is complete the
         * moment the clinic exists rather than after ten more screens.
         */
        stores: stores
          .filter((store) => store.name.trim() !== '')
          .map((store) => {
            const { open: _open, ...rest } = store;
            void _open;
            if (!hasDetails(store)) return rest.name.trim();
            return {
              ...storeProfilePayload(store),
              name: rest.name.trim(),
              wordpressCategory: store.wordpressCategory,
            };
          }),
      });
      // Straight to the new record, which is where the next action lives:
      // adding its administrator.
      void navigate(`/admin/clinics/${clinic.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields[0] ?? err.message) : '登録できませんでした');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card
      title="クリニックを新規登録"
      caption="1〜4の順に入力します。必須はクリニック名だけで、ほかは後から変更できます。"
    >
      <form className="regform" onSubmit={(event) => void submit(event)}>
        <Step
          n={1}
          title="クリニック"
          note="サイトURLを入れて読み取ると、以下の項目に反映されます。"
        >
          <div className="editpanel__body">
            <Field label="サイトURL" htmlFor="siteUrl" span="full">
              <input
                id="siteUrl"
                type="url"
                inputMode="url"
                maxLength={2000}
                value={siteUrl}
                placeholder="https://example.clinic"
                onChange={(e) => setSiteUrl(e.target.value)}
              />
            </Field>
            {/*
              Its own row, not a cell.

              ReadFromSite already renders a `field field--full`, so wrapping it
              in a second field put a full-width block inside one grid column:
              the button squeezed to a grey stub, its sentence wrapped to three
              lines, and クリニック名 was pushed into a third column beside them.
              It spans the row on its own, directly under the URL it reads.
            */}
            <ReadFromSite kind="clinic" url={siteUrl} labels={SITE_LABELS} onFilled={fillFromSite} />

            <Field label="クリニック名" htmlFor="name" required>
              <input
                id="name"
                required
                maxLength={200}
                value={name}
                placeholder="例：東京ビューティークリニック"
                onChange={(e) => setName(e.target.value)}
              />
            </Field>

            <Field label="契約プラン" htmlFor="plan" hint="記事と画像の月間上限を決めます。">
              <Select
                id="plan"
                value={planId}
                onChange={setPlanId}
                options={[
                  { value: '', label: '最安のプラン（自動）' },
                  // The limits on the option, because a plan name says nothing
                  // about what it allows and this is where the choice is made.
                  ...plans.items.map((plan) => ({
                    value: plan.id,
                    label: `${plan.name}（記事 ${limitText(plan.limits.articles_per_month)}/店舗・店舗数 ${limitText(plan.limits.stores)}）`,
                  })),
                ]}
              />
            </Field>
          </div>
        </Step>

        {/*
          Registered with the clinic rather than "later, in settings": an article
          may name only what is registered, so a clinic created without its
          operating entity cannot say who runs it.
        */}
        <Step
          n={2}
          title="運営法人"
          note="医療広告ガイドラインが求める記載元です。記事の署名に使われます。"
        >
          <div className="editpanel__body">
            <ClinicProfileFields idPrefix="new" value={profile} onChange={setProfile} />
          </div>
        </Step>

        <Step n={3} title="記事の初期設定" note="全店舗の記事が引き継ぐ既定値です。店舗ごとに上書きできます。">
          <div className="editpanel__body">
            <Field label="サイト名" htmlFor="siteName">
              <input
                id="siteName"
                maxLength={200}
                value={siteName}
                placeholder="例：東京ビューティークリニック公式ブログ"
                onChange={(e) => setSiteName(e.target.value)}
              />
            </Field>

            <Field label="標準トーン" htmlFor="tone">
              <Select
                id="tone"
                value={defaultTone}
                onChange={setDefaultTone}
                options={TONES.map((tone) => ({ value: tone.value, label: tone.label }))}
              />
            </Field>

            <Field label="WordPressカテゴリー" htmlFor="wpCategory">
              <input
                id="wpCategory"
                maxLength={120}
                value={wordpressCategory}
                placeholder="例：美容コラム"
                onChange={(e) => setWordpressCategory(e.target.value)}
              />
            </Field>

            <Field
              label="対策キーワード"
              htmlFor="clinic-keywords"
              span="full"
              hint="すべての記事に渡されます。"
            >
              <KeywordInput
                id="clinic-keywords"
                value={defaultKeywords}
                onChange={setDefaultKeywords}
                placeholder="例：美容医療"
              />
            </Field>
          </div>
        </Step>

        {/*
          Names are enough, and details are here for whoever has them.

          A store used to be a single text box, so registering one meant coming
          back through the store screen to type the address it is found by. The
          details are folded away rather than absent: the common registration is
          still one line per branch, and the operator who has the addresses in
          front of them can finish the job in one pass.
        */}
        <Step
          n={4}
          title="店舗"
          note="店舗名だけで登録できます。住所などは「詳細」を開いて入力するか、後から店舗画面で追加できます。"
        >
          <div className="storelist">
            {stores.map((store, index) => (
              // Index as key: these rows have no identity until they are saved,
              // and the value itself is not unique while someone is still typing.
              <div className="storecard" key={index}>
                <div className="storerow">
                  <Field label={`店舗名 ${index + 1}`} htmlFor={`store-${index}`}>
                    <input
                      id={`store-${index}`}
                      maxLength={200}
                      value={store.name}
                      placeholder="例：渋谷院"
                      onChange={(e) => setStore(index, { name: e.target.value })}
                    />
                  </Field>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    aria-expanded={store.open}
                    onClick={() => setStore(index, { open: !store.open })}
                  >
                    {store.open ? '詳細を閉じる' : '詳細'}
                    {!store.open && hasDetails(store) ? ' ●' : ''}
                  </button>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    onClick={() => removeStore(index)}
                    aria-label={`店舗名 ${index + 1} を削除`}
                  >
                    削除
                  </button>
                </div>

                {store.open && (
                  <div className="editpanel__body storecard__details">
                    <StoreProfileFields
                      idPrefix={`store-${index}`}
                      value={store}
                      onChange={(next) => setStore(index, next)}
                    />
                    <Field
                      label="WordPressカテゴリー"
                      htmlFor={`store-${index}-wp`}
                      hint="空欄ならクリニックの設定を使います。"
                    >
                      <input
                        id={`store-${index}-wp`}
                        maxLength={120}
                        value={store.wordpressCategory}
                        onChange={(e) => setStore(index, { wordpressCategory: e.target.value })}
                      />
                    </Field>
                  </div>
                )}
              </div>
            ))}
            <button className="btn btn--ghost btn--sm" type="button" onClick={addStore}>
              ＋ 店舗を追加
            </button>
          </div>
        </Step>

        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}

        <div className="editpanel__actions">
          <button className="btn" type="submit" disabled={saving}>
            {saving ? '登録中…' : '登録する'}
          </button>
          <button className="btn btn--ghost" type="button" onClick={() => void navigate(-1)}>
            キャンセル
          </button>
          <span className="muted small">登録後、管理ユーザーを追加できます。</span>
        </div>
      </form>
    </Card>
  );
}
